import {
    ArrowBack,
    EventRepeatOutlined,
    ForwardToInboxOutlined,
    PersonOutlined,
    PsychologyOutlined,
} from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import Page from "components/Navigation/Page";
import AcxButton from "components/UI/AcxButton";
import SideControlsPage from "Layouts/SideControls/SideControlsPage";
import { MAIN_APP_BAR_HEIGHT_PX } from "components/Navigation/appBarHeight";
import { observer } from "mobx-react";
import React from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import KboClientBrain from "../ClientBrain/KboClientBrain";
import MySubscriptionsPage from "./MySubscriptionsPage";
import { SendLogPage } from "./SendLogPage";
import { SubscriptionsPage } from "./SubscriptionsPage";
import UserSettingsPage from "./UserSettingsPage";
import { useKboSettingsReturn } from "./useKboSettingsReturn";

type NavigationItem = {
    title: string;
    path: string;
    id: string;
    icon: typeof PersonOutlined;
    page: React.ReactNode;
    permissions: string[];
    /** Visible only to internal Authenticx users. */
    authcxOnly?: boolean;
};

const navigationItems: NavigationItem[] = [
    {
        title: "My Subscriptions",
        path: "my-subscriptions",
        id: "kbo-settings-my-subscriptions",
        icon: EventRepeatOutlined,
        page: <MySubscriptionsPage />,
        permissions: ["View KBo"],
        authcxOnly: true,
    },
    {
        title: "User Context",
        path: "user",
        id: "kbo-settings-user-context",
        icon: PersonOutlined,
        page: <UserSettingsPage />,
        permissions: ["View KBo"],
    },
    {
        title: "Client Context",
        path: "client-context",
        id: "kbo-settings-client-context",
        icon: PsychologyOutlined,
        page: <KboClientBrain />,
        permissions: ["Client Brain"],
    },
    {
        title: "Send Log",
        path: "send-log",
        id: "kbo-settings-send-log",
        icon: ForwardToInboxOutlined,
        page: <SendLogPage />,
        permissions: ["Client Brain"],
    },
    {
        title: "All Subscriptions",
        path: "subscriptions",
        id: "kbo-settings-subscriptions",
        icon: EventRepeatOutlined,
        page: <SubscriptionsPage />,
        permissions: ["Client Brain"],
    },
];

const KboSettingsLayout = observer(() => {
    const location = useLocation();
    const authStore = useStore(AuthStore);
    const handleBackToKbo = useKboSettingsReturn();

    if (
        !authStore.canUserView("Client Brain") &&
        !authStore.canUserView("View KBo")
    ) {
        return <Navigate to="/app/kbo/chats" replace />;
    }

    const currentNavItem = location.pathname.split("/").pop();
    const visibleNavigationItems = navigationItems.filter((i) => {
        if (i.authcxOnly && !authStore.isAuthcxUser()) return false;
        if (
            i.permissions &&
            i.permissions.length > 0 &&
            !i.permissions.every((p) => authStore.canUserView(p))
        )
            return false;
        return true;
    });

    return (
        <SideControlsPage
            drawerLocation="left"
            containerOffsetPixels={MAIN_APP_BAR_HEIGHT_PX}
            mainGridAlignItems="stretch"
            mainGridSpacing={0}
            mainSx={{ padding: 0 }}
            drawerContent={
                <Stack paddingInline={2} paddingBlock={2} rowGap={2}>
                    <AcxButton
                        id="kbo-settings-back-to-kbo"
                        color="white"
                        variant="text"
                        disableRipple
                        startIcon={
                            <ArrowBack
                                sx={(theme) => ({
                                    fontSize: 12,
                                    color: theme.palette.gray[400],
                                })}
                            />
                        }
                        onClick={handleBackToKbo}
                        containerStyle={{ alignSelf: "flex-start" }}
                        sx={(theme) => ({
                            background: "transparent",
                            color: theme.palette.black.main,
                            padding: 0,
                            minWidth: "unset",
                            fontSize: 14,
                            fontWeight: 700,
                            ":hover": { background: "transparent" },
                        })}
                    >
                        Back to KBo
                    </AcxButton>
                    <Stack rowGap={0.5}>
                        <Typography
                            variant="overline"
                            sx={(theme) => ({
                                color: theme.palette.gray[400],
                                fontWeight: 600,
                                letterSpacing: "0.06em",
                            })}
                        >
                            Settings
                        </Typography>
                        {visibleNavigationItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = currentNavItem === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    id={item.id}
                                    to={item.path}
                                    style={{ textDecoration: "none" }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        columnGap={1}
                                        paddingInline={2}
                                        paddingBlock={1}
                                        sx={(theme) => ({
                                            borderRadius: "8px",
                                            backgroundColor: isActive
                                                ? theme.palette.primary.light
                                                : "transparent",
                                            "&:hover": {
                                                backgroundColor: isActive
                                                    ? theme.palette.primary
                                                          .light
                                                    : theme.palette.gray[100],
                                            },
                                        })}
                                    >
                                        <Icon
                                            sx={(theme) => ({
                                                fontSize: 16,
                                                color: isActive
                                                    ? theme.palette.primary.main
                                                    : theme.palette.gray[500],
                                            })}
                                        />
                                        <Typography
                                            sx={(theme) => ({
                                                fontSize: 14,
                                                fontWeight: isActive
                                                    ? 700
                                                    : 500,
                                                color: isActive
                                                    ? theme.palette.primary.main
                                                    : theme.palette.black.main,
                                            })}
                                        >
                                            {item.title}
                                        </Typography>
                                    </Stack>
                                </Link>
                            );
                        })}
                    </Stack>
                </Stack>
            }
            mainContent={
                <Routes>
                    <Route
                        path=""
                        element={
                            <Navigate
                                to={visibleNavigationItems.at(0)?.path ?? "../"}
                                replace
                            />
                        }
                    />
                    {visibleNavigationItems.map((item) => {
                        return (
                            <Route
                                path={item.path}
                                element={
                                    <Page title={`${item.title} | Authenticx`}>
                                        {item.page}
                                    </Page>
                                }
                            />
                        );
                    })}
                </Routes>
            }
        />
    );
});

export default KboSettingsLayout;
